import { useEffect, useState } from "react";
import { Tag } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fetchOrderPrices, findPrice } from "@/lib/orderPrices";
import { TRADE_CATEGORIES, orderModels as modelsOf, NO_STORAGE } from "@/lib/tradeCatalog";
import { chip } from "@/components/site/OrderSpecFields";
const num = "w-full min-w-0 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-white/40";

const RangeCell = ({ model, storage, condition, entry, onSaved }) => {
  const [lo, setLo] = useState(entry?.min ?? "");
  const [hi, setHi] = useState(entry?.max ?? "");
  const id = `${storage}-${condition}`.toLowerCase();

  const save = async (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    const min = lo === "" ? null : Number(lo);
    const max = hi === "" ? null : Number(hi);
    if (min === (entry?.min ?? null) && max === (entry?.max ?? null)) return;
    try {
      const { data } = await api.put("/admin/order-prices", { model, storage, condition, min, max });
      setLo(data.min ?? "");
      setHi(data.max ?? "");
      onSaved(data);
      toast.success(data.min == null ? "Preço removido" : "Preço salvo");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="flex items-center gap-1.5" onBlur={save}>
      <input data-testid={`price-min-${id}`} type="number" inputMode="numeric" min="0" placeholder="de" value={lo} onChange={(e) => setLo(e.target.value)} className={num} />
      <input data-testid={`price-max-${id}`} type="number" inputMode="numeric" min="0" placeholder="até" value={hi} onChange={(e) => setHi(e.target.value)} className={num} />
    </div>
  );
};

export const OrderPricesTable = () => {
  const [prices, setPrices] = useState(null);
  const [category, setCategory] = useState("iphone");
  const models = modelsOf(category);
  const [model, setModel] = useState(models[0].name);
  const spec = models.find((m) => m.name === model) || models[0];

  useEffect(() => {
    fetchOrderPrices(true).then(setPrices).catch(() => setPrices([]));
  }, []);

  const onSaved = (p) => setPrices((list) => [...list.filter((x) => !(x.model === p.model && x.storage === p.storage && x.condition === p.condition)), ...(p.min == null ? [] : [p])]);
  const filled = (m) => (prices || []).filter((p) => p.model === m).length;
  const conditions = spec.conditions;
  const cols = conditions.length > 1 ? "sm:grid-cols-[90px_1fr_1fr]" : "sm:grid-cols-[90px_1fr]";

  return (
    <section data-testid="admin-order-prices" className="mt-8 rounded-[24px] border border-white/10 bg-[#111113] p-5 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <Tag className="h-5 w-5 text-white" />
          <h2 className="font-display text-xl font-bold">Preços de encomenda</h2>
        </div>
        <select data-testid="admin-prices-model" value={spec.name} onChange={(e) => setModel(e.target.value)} className="max-w-full rounded-full border border-white/15 bg-transparent px-4 py-2 text-sm text-white outline-none">
          {models.map((m) => <option key={m.name} value={m.name} className="bg-zinc-900">{m.name}{filled(m.name) ? ` (${filled(m.name)})` : ""}</option>)}
        </select>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {TRADE_CATEGORIES.map((c) => (
          <button type="button" key={c.id} data-testid={`admin-prices-category-${c.id}`} onClick={() => { setCategory(c.id); setModel(modelsOf(c.id)[0].name); }} className={chip(category === c.id)}>{c.label}</button>
        ))}
      </div>
      <p className="mt-3 text-sm text-zinc-400">Preencha "de" e "até" (em reais). O cliente vê a estimativa no "Fazer pedido" e o preço médio na Troca. Deixe vazio para não mostrar.</p>
      {prices && <div className="mt-4 space-y-3">
        <div className={`hidden gap-3 text-xs text-zinc-400 sm:grid ${cols}`}>
          <span />
          {conditions.map(([, label]) => <span key={label}>{label}</span>)}
        </div>
        {spec.storages.map((s) => (
          <div key={`${spec.name}-${s}`} data-testid={`admin-price-row-${s.toLowerCase()}`} className={`grid grid-cols-1 items-center gap-2 border-t border-white/10 pt-3 sm:gap-3 ${cols}`}>
            <span className="font-semibold">{s === NO_STORAGE ? "Preço" : s}</span>
            {conditions.map(([c, label]) => (
              <div key={c}>
                <span className="mb-1 block text-[11px] text-zinc-400 sm:hidden">{label}</span>
                <RangeCell key={`${spec.name}-${s}-${c}`} model={spec.name} storage={s} condition={c} entry={findPrice(prices, spec.name, s, c)} onSaved={onSaved} />
              </div>
            ))}
          </div>
        ))}
      </div>}
    </section>
  );
};
